import CaretRightOutlined from '@ant-design/icons/CaretRightOutlined';
import Alert from 'antd/lib/alert';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import DatePicker from 'antd/lib/date-picker';
import Form from 'antd/lib/form';
import List from 'antd/lib/list';
import Modal from 'antd/lib/modal';
import Spin from 'antd/lib/spin';
import Typography from 'antd/lib/typography';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { toUniversalTime } from '../../domain/event';
import { INomination } from '../../domain/nomination/interface';
import { useNominationReadinessTrackCreateMutation, useNominationReadinessValidateMutation } from '../../domain/nomination/readiness/queries';
import { getComputerFormattedTime } from '../../domain/date/date';

interface IScheduleNominationReadinessModalProps {
    nominations: INomination[];
    onClose: (isRefreshNeeded?: boolean) => void;
}

interface IScheduleNominationReadinessFormData {
    dateToSend: Dayjs;
}

export const ScheduleNominationReadinessModal: React.FC<IScheduleNominationReadinessModalProps> = ({ nominations, onClose }) => {
    const { notification } = App.useApp();
    const [form] = Form.useForm<IScheduleNominationReadinessFormData>();
    const [loading, setLoading] = useState(false);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneLabel = getComputerFormattedTime(new Date(), 'zzz');

    const createMutation = useNominationReadinessTrackCreateMutation();
    const validateMutation = useNominationReadinessValidateMutation();

    const validationError = validateMutation.error?.message ?? null;

    const disabledDate = useCallback((current: Dayjs) => {
        const today = dayjs().startOf('day');
        const sixMonthsLater = dayjs().add(6, 'month');

        return current < today || current > sixMonthsLater;
    }, []);

    const disabledTime = useCallback((current: Dayjs) => {
        if (!current) return { disabledHours: () => [] };

        const isToday = current.isSame(dayjs(), 'day');
        if (!isToday) return { disabledHours: () => [] };

        // If selected date is today. We disable hours up to and including the current hour
        const currentHour = dayjs().hour();
        return { disabledHours: () => getDisabledHours(currentHour) };
    }, []);

    const onFormFinish = async (data: IScheduleNominationReadinessFormData) => {
        setLoading(true);

        try {
            await createMutation.mutateAsync({
                nominationIds: nominations.map(nom => nom.salesforce_id),
                dateToSend: toUniversalTime(data.dateToSend.toDate()),
                timezone: timezone,
            });

            notification.success({ key: 'info', message: 'Nomination Readiness Scheduled!' });
            onClose(true);
        } catch (err: any) {
            notification.error({ key: 'error', message: err.message || 'Error to Schedule Nomination Readiness!' });
        }

        setLoading(false);
    };

    useEffect(() => {
        const nominationIds = nominations.map(n => n.salesforce_id);

        validateMutation.mutate(nominationIds);
    }, []);

    return (
        <>
            <Modal
                open
                title="Schedule Readiness Emails"
                width={450}
                destroyOnHidden
                onCancel={() => onClose()}
                footer={[
                    <Button key="cancel" onClick={() => onClose()}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={validateMutation.isLoading}
                        disabled={!!validationError || validateMutation.isLoading}
                        onClick={form.submit}
                    >
                        Schedule
                    </Button>,
                ]}
            >
                <Typography.Text strong>
                    Users will receive an email on the selected date to confirm their readiness for the nominations listed below.
                </Typography.Text>

                <br />
                <br />

                <Collapse accordion size="small" expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}>
                    <Collapse.Panel
                        key="resources"
                        collapsible={loading ? 'disabled' : 'header'}
                        header={
                            <Spin spinning={loading}>
                                <Typography.Text>
                                    Affected {nominations.length} nomination{nominations.length > 1 && 's'}
                                </Typography.Text>
                            </Spin>
                        }
                    >
                        <List
                            size="small"
                            bordered={false}
                            dataSource={nominations}
                            style={{ maxHeight: 350, overflowY: 'scroll' }}
                            renderItem={(item, idx) => {
                                const siteName = item.meter_enrollment?.meter?.site?.site_name || '';
                                const text = `Site: ${siteName}, Nomination: ${item.name} (${item.nomination_kw} kW)`;

                                return (
                                    <List.Item key={idx}>
                                        <Typography.Text>{text}</Typography.Text>
                                    </List.Item>
                                );
                            }}
                        />
                    </Collapse.Panel>
                </Collapse>

                <br />

                {validationError && <Alert showIcon type="error" message={validationError} style={{ marginBottom: 16 }} />}

                <Form form={form} layout="vertical" onFinish={onFormFinish}>
                    <Form.Item
                        name="dateToSend"
                        label={<Typography.Text strong>Date & Time to send</Typography.Text>}
                        rules={[{ required: true, message: 'Please select date and time!' }]}
                        hasFeedback
                    >
                        <DatePicker
                            size="large"
                            format="DD/MMM/YYYY hh A"
                            disabledDate={disabledDate}
                            showTime={{ format: 'hh A', disabledTime }}
                            style={{ width: '100%' }}
                            allowClear={false}
                            showNow={false}
                        />
                    </Form.Item>
                </Form>

                <Alert
                    showIcon
                    type="warning"
                    message={
                        <span>
                            Email notifications will be sent at selected datetime in your local timezone - <b>{timezoneLabel}</b>.
                        </span>
                    }
                />
            </Modal>
        </>
    );
};

function getDisabledHours(currentHour: number): number[] {
    return Array.from({ length: currentHour + 1 }, (_, i) => i);
}
